"use client"

import { useEffect, useMemo, useState } from "react"
import { Meeting } from "@/schema"
import { Button, Calendar, DatePopover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { format, isValid, setHours, setMinutes } from "date-fns"
import { CalendarClock, Loader2 } from "lucide-react"

import { useUpdateMeeting } from "@/hooks/api/meetings"
import { TimeSelect } from "@/components/_ui/time-picker/time-select"

type DateDisplayFormat = "short" | "long"

interface MeetingDateTimeEditorProps {
  meeting: Meeting
  className?: string
  textClassName?: string
  displayFormat?: DateDisplayFormat
  showEndTime?: boolean
}

function getTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
}

function parseDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null

  const parsed = value instanceof Date ? value : new Date(value)
  return isValid(parsed) ? parsed : null
}

function applyTimeToDate(date: Date, timeString: string): Date | null {
  if (!isValid(date)) return null

  const match = timeString.match(/^([01]\d|2[0-3]):([0-5]\d)$/)
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  const updatedDate = setMinutes(setHours(date, hours), minutes)

  return isValid(updatedDate) ? updatedDate : null
}

function buildDateTimeText(meeting: Meeting, displayFormat: DateDisplayFormat, showEndTime: boolean): string {
  const startTime = parseDate(meeting.start_time)
  if (!startTime) return "Date unavailable"

  const formatString = displayFormat === "long" ? "MMMM d, yyyy 'at' h:mm a" : "MMM d, yyyy · h:mm a"
  const startText = format(startTime, formatString)

  if (!showEndTime || !meeting.end_time) {
    return startText
  }

  const endTime = parseDate(meeting.end_time)
  if (!endTime) return startText

  return `${startText} - ${format(endTime, "h:mm a")}`
}

function isCalendarManagedMeeting(meeting: Meeting): boolean {
  return Boolean(meeting.calendar_event_id || meeting.has_event_occurrence || meeting.event_occurrence_id)
}

export default function MeetingDateTimeEditor({
  meeting,
  className,
  textClassName,
  displayFormat = "short",
  showEndTime = true,
}: MeetingDateTimeEditorProps) {
  const updateMeeting = useUpdateMeeting()
  const [isOpen, setIsOpen] = useState(false)

  const startTime = useMemo(() => parseDate(meeting.start_time), [meeting.start_time])
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(startTime ?? undefined)
  const [selectedTime, setSelectedTime] = useState<string>(startTime ? getTimeString(startTime) : "")

  const currentStartTimeMs = startTime?.getTime()
  const meetingDurationMs = useMemo(() => {
    if (!meeting.end_time || currentStartTimeMs === undefined) return null
    if (Number.isNaN(currentStartTimeMs)) return null

    const endTime = parseDate(meeting.end_time)
    if (!endTime) return null

    const endTimeMs = endTime.getTime()

    if (Number.isNaN(endTimeMs) || endTimeMs < currentStartTimeMs) {
      return null
    }

    return endTimeMs - currentStartTimeMs
  }, [meeting.end_time, currentStartTimeMs])

  useEffect(() => {
    if (!startTime) {
      setSelectedDate(undefined)
      setSelectedTime("")
      return
    }

    setSelectedDate(new Date(startTime))
    setSelectedTime(getTimeString(startTime))
  }, [currentStartTimeMs, startTime])

  const calendarManaged = isCalendarManagedMeeting(meeting)
  const dateTimeText = buildDateTimeText(meeting, displayFormat, showEndTime)

  async function handleSave() {
    if (!selectedDate || !isValid(selectedDate)) return

    const newStartTime = applyTimeToDate(selectedDate, selectedTime)
    if (!newStartTime) return

    const updateData: Partial<Meeting> = { start_time: newStartTime }

    if (meetingDurationMs !== null) {
      updateData.end_time = new Date(newStartTime.getTime() + meetingDurationMs)
    }

    try {
      await updateMeeting.mutateAsync({
        meetingId: meeting.id,
        data: updateData,
      })
      setIsOpen(false)
    } catch {
      // Handled by global mutation error handling.
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    setIsOpen(nextOpen)

    if (nextOpen) {
      const resetDate = parseDate(meeting.start_time)
      setSelectedDate(resetDate ? new Date(resetDate) : undefined)
      setSelectedTime(resetDate ? getTimeString(resetDate) : "")
    }
  }

  const canSave = Boolean(selectedDate && isValid(selectedDate) && applyTimeToDate(selectedDate, selectedTime))

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {!calendarManaged && (
        <DatePopover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              className="text-muted-foreground hover:text-foreground h-auto gap-1.5 px-1.5 py-0.5 font-normal"
              onClick={(event) => event.stopPropagation()}
              tooltip="Edit date & time"
              aria-label="Edit date and time"
            >
              <span className={textClassName}>{dateTimeText}</span>
              <CalendarClock />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" onClick={(event) => event.stopPropagation()}>
            <div className="p-3">
              <h4 className="mb-2 text-sm font-medium">Edit date & time</h4>
              <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} initialFocus />
              <div className="mt-3">
                <label className="text-muted-foreground mb-1 block text-xs">Time</label>
                <TimeSelect
                  value={selectedTime}
                  onChange={(value) => setSelectedTime(value)}
                  size="sm"
                  startHour={0}
                  endHour={23}
                  intervalMinutes={15}
                />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={!canSave || updateMeeting.isPending}>
                  {updateMeeting.isPending && <Loader2 className="animate-spin" />}
                  Save
                </Button>
              </div>
            </div>
          </PopoverContent>
        </DatePopover>
      )}

      {calendarManaged && <span className={textClassName}>{dateTimeText}</span>}
    </div>
  )
}
